import {
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable()
export class ApiService {
  public projectFormFields = new BehaviorSubject<any>(null);

  api = environment.apiUrl;
  private messageResponseTime = 10000;
  private messageMsPerChar = 500;

  constructor(
    private readonly Http: HttpClient,
    private readonly auth: AuthService,
    private SnackBar: MatSnackBar,
  ) {}

  post(endpoint, body, endpointUrl?: string) {
    endpointUrl = endpointUrl || environment.apiUrl;
    return this.request(`${endpointUrl}/${endpoint}`, 'POST', body);
  }

  put(endpoint, body, endpointUrl?: string) {
    endpointUrl = endpointUrl || environment.apiUrl;
    return this.request(`${endpointUrl}/${endpoint}`, 'PUT', body);
  }

  patch(endpoint, body, endpointUrl?: string) {
    endpointUrl = endpointUrl || environment.apiUrl;
    return this.request(`${endpointUrl}/${endpoint}`, 'PATCH', body);
  }

  get(endpoint: string, endpointUrl?: string) {
    endpointUrl = endpointUrl || environment.apiUrl;
    return this.request(`${endpointUrl}/${endpoint}`);
  }

  public getWithoutPageEventFilter(
    endpoint: string,
    filter?: {},
    endpointUrl?: string,
  ) {
    const method = 'GET';
    endpointUrl = endpointUrl || environment.apiUrl;
    return this.request(`${endpointUrl}/${endpoint}`, method, {
      ...filter,
    });
  }

  public getWithPageEventFilter(
    endpoint: string,
    PageEvent: PageEvent,
    filter?: {},
    endpointUrl?: string,
  ) {
    const method = 'GET';
    const { pageIndex = 0, pageSize = 24 } = PageEvent || {};
    endpointUrl = endpointUrl || environment.apiUrl;
    return this.request(`${endpointUrl}/${endpoint}`, method, {
      pageIndex,
      pageSize,
      ...filter,
    });
  }

  public getWithPageEvent(
    endpoint: string,
    PageEvent: PageEvent,
    endpointUrl?: string,
  ) {
    const method = 'GET';
    endpointUrl = endpointUrl || environment.apiUrl;

    if (!!PageEvent) {
      if (endpoint.includes('page-index')) {
        endpoint = endpoint.replace('/page-size/24/page-index/0', '');
      }

      endpoint += `/page-size/${PageEvent.pageSize}/page-index/${PageEvent.pageIndex}`;
    }
    return this.request(`${endpointUrl}/${endpoint}`, method);
  }

  delete(endpoint, endpointUrl?: string, body?) {
    endpointUrl = endpointUrl || environment.apiUrl;
    return this.request(`${endpointUrl}/${endpoint}`, 'DELETE', body);
  }

  private async request(
    endpoint: string,
    method = 'GET',
    params?: {},
  ): Promise<any> {
    const tokenValid = await this.auth.refreshToken();

    if (!tokenValid) {
      if (environment.verboseLogging) {
        console.log('Token refresh failed, redirecting to login');
      }
      await this.auth.handleUnauthorized();
      throw new Error('unauthorised');
    }

    const options = this.httpOptions(method, { ...params });
    const url = endpoint;

    try {
      return await firstValueFrom(this.Http.request(method, url, options));
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (environment.verboseLogging) {
          console.log('API request returned 401, clearing session');
        }
        await this.auth.handleUnauthorized();
        throw new Error('unauthorised');
      }

      throw error;
    }
  }

  private httpOptions(method: string, params?: { [key: string]: string }) {
    const optionKey = method === 'GET' ? 'params' : 'body';
    const token = this.auth.getToken();

    const options = Object.keys(params || {}).reduce(
      (response, key: string) => {
        if (params[key] !== null) {
          response[key] = params[key];
        }

        return response;
      },
      {},
    );

    return {
      headers: { Authorization: `${token}` },
      [optionKey]: options,
    };
  }

  public handleError(message: any = 'An error occurred.'): Promise<void> {
    const config: MatSnackBarConfig = new MatSnackBarConfig();

    config.duration = this.getMessageTime(message);
    config.panelClass = ['error'];
    if (message?.error) {
      message = message.error?.error;
    } else if (message?.message) {
      message = message.message;
    }
    return firstValueFrom(
      this.SnackBar.open(message, 'Close', config).afterOpened(),
    );
  }

  public handleSuccess(message: string) {
    const config: MatSnackBarConfig = new MatSnackBarConfig();

    config.duration = this.getMessageTime(message);
    config.panelClass = ['success'];

    return firstValueFrom(
      this.SnackBar.open(message, 'Close', config).afterOpened(),
    );
  }

  private getMessageTime(message: string) {
    return this.messageResponseTime + message.length * this.messageMsPerChar;
  }
}
